// Shims de DEEP-LINK da ficha-overlay (docs/plan/tv-ficha-overlay.md §4, Caso A).
//
// As rotas `/movies/:id` e `/vod-player/:id` deixaram de RENDERIZAR a ficha/player
// (agora são overlays acima do RouterProvider). Viram shims: no mount empilham a
// camada E redirecionam pra o catálogo do tipo com `replace`, pra ESTABELECER uma
// origem roteada por baixo. Como a ficha-overlay é `fixed; inset:0` opaca, o
// deep-link frio (reload/bookmark no WEB, recovery de ChunkLoadError) cobre a tela
// inteira mesmo sem origem "de verdade" atrás — e quando a pilha esvazia, o Back
// cai no catálogo que o shim deixou por baixo (foco no `nav-*` via restoreSafeKey).
//
// Ordem OBRIGATÓRIA: `openDetail`/`pushPlayer` ANTES do `navigate` — os dois no
// MESMO effect imperativo. Um `<Navigate>` declarativo dispara a navegação num
// effect FILHO (roda antes do effect deste componente) e desmontaria o shim antes
// do push. O guard por ref evita duplo-push no strict-mode (effect 2x em dev).

import { useEffect, useRef } from 'react'
import { useNavigate, useParams, useSearchParams } from 'react-router-dom'

import { openDetail, pushPlayer } from '../detail-overlay-store'

// Origem por baixo do deep-link = catálogo do tipo (recomendação do plano §13, o
// "voltar" mais coerente com o conteúdo aberto). Filme → /movies, série → /series.
const MOVIE_ORIGIN = '/movies'
const MOVIE_SAFE_KEY = 'nav-movies'
const SERIES_ORIGIN = '/series'
const SERIES_SAFE_KEY = 'nav-series'

/** `/movies/:id` → abre a ficha do filme + estabelece /movies por baixo. */
export function MovieDetailShim() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const done = useRef(false)

  useEffect(() => {
    if (done.current) return
    done.current = true
    if (id) {
      openDetail({
        kind: 'movie-detail',
        id,
        // Deep-link frio: NÃO há card de origem pra devolver — a rede de
        // segurança (nav-movies) assume no fecho.
        restoreFocusKey: '',
        restoreSafeKey: MOVIE_SAFE_KEY,
      })
    }
    navigate(MOVIE_ORIGIN, { replace: true })
  }, [id, navigate])

  return null
}

/** `/vod-player/:id` → abre a pilha [ficha, player] + /movies por baixo. */
export function VodPlayerShim() {
  const { id } = useParams<{ id: string }>()
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()
  const done = useRef(false)

  useEffect(() => {
    if (done.current) return
    done.current = true
    if (id) {
      const resume = Number(searchParams.get('resume') ?? 0)
      openDetail({
        kind: 'movie-detail',
        id,
        restoreFocusKey: '',
        restoreSafeKey: MOVIE_SAFE_KEY,
      })
      pushPlayer(
        resume > 0
          ? { kind: 'vod-player', sourceId: id, resumeAt: resume }
          : { kind: 'vod-player', sourceId: id },
      )
    }
    navigate(MOVIE_ORIGIN, { replace: true })
  }, [id, searchParams, navigate])

  return null
}

/** `/series/:id` → abre a ficha da série + estabelece /series por baixo. */
export function SeriesDetailShim() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const done = useRef(false)

  useEffect(() => {
    if (done.current) return
    done.current = true
    if (id) {
      openDetail({
        kind: 'series-detail',
        id,
        // Deep-link frio: sem card de origem → a rede de segurança (nav-series)
        // assume no fecho.
        restoreFocusKey: '',
        restoreSafeKey: SERIES_SAFE_KEY,
      })
    }
    navigate(SERIES_ORIGIN, { replace: true })
  }, [id, navigate])

  return null
}

/** `/series-player/:seriesId/:episodeId` → abre [ficha, player] + /series por baixo. */
export function SeriesPlayerShim() {
  const { seriesId, episodeId } = useParams<{ seriesId: string; episodeId: string }>()
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()
  const done = useRef(false)

  useEffect(() => {
    if (done.current) return
    done.current = true
    if (seriesId && episodeId) {
      const resume = Number(searchParams.get('resume') ?? 0)
      openDetail({
        kind: 'series-detail',
        id: seriesId,
        restoreFocusKey: '',
        restoreSafeKey: SERIES_SAFE_KEY,
      })
      pushPlayer(
        resume > 0
          ? { kind: 'series-player', seriesId, episodeId, resumeAt: resume }
          : { kind: 'series-player', seriesId, episodeId },
      )
    }
    navigate(SERIES_ORIGIN, { replace: true })
  }, [seriesId, episodeId, searchParams, navigate])

  return null
}
